import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import {
  formatEntityNumberRange,
  getEntityNumberStarts,
} from '../shapes/entityNumbering'
import { MAX_ENTITY_COUNT } from '../constants'
import { toggleSelection } from '../shapes/shapeInteraction'
import ShapeListRow from './ShapeListRow'

type Props = {
  shapes: VisualShape[]
  canEdit: boolean
  aspect: number
  selectedIds: string[]
  onSelectIds: (ids: string[]) => void
  onChange: (shapes: VisualShape[]) => void
}

const ShapeList = ({
  shapes,
  canEdit,
  aspect,
  selectedIds,
  onSelectIds,
  onChange,
}: Props) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )

  const reversedShapes = [...shapes].reverse()
  const entityNumberStarts = getEntityNumberStarts(shapes)

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const sourceIndex = reversedShapes.findIndex(
      (s) => s.id === String(active.id),
    )
    const targetIndex = reversedShapes.findIndex(
      (s) => s.id === String(over.id),
    )
    if (sourceIndex < 0 || targetIndex < 0) return
    const reordered = arrayMove(reversedShapes, sourceIndex, targetIndex)
    onChange([...reordered].reverse())
  }

  if (shapes.length === 0) {
    return (
      <div className='py-4 text-center text-sm text-muted-foreground'>
        No objects yet
      </div>
    )
  }

  return (
    <div className='w-full'>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={reversedShapes.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className='flex flex-col gap-1'>
            {reversedShapes.map((shape) => {
              const start = entityNumberStarts.get(shape.id)
              const entityRange =
                start !== undefined
                  ? formatEntityNumberRange(
                      start,
                      Math.min(shape.entity_count ?? 0, MAX_ENTITY_COUNT),
                    )
                  : undefined

              return (
                <ShapeListRow
                  key={shape.id}
                  shape={shape}
                  isSelected={selectedIds.includes(shape.id)}
                  canEdit={canEdit}
                  aspect={aspect}
                  entityRange={entityRange}
                  onSelect={(additive) =>
                    onSelectIds(
                      additive
                        ? toggleSelection(selectedIds, shape.id)
                        : [shape.id],
                    )
                  }
                />
              )
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}

export default ShapeList
